import { MODULE_CATALOG } from '@platform/entitlements';
import type { AuditEntry } from '../../api';

/**
 * Turns an audit row into something a person can read.
 *
 * The database stores what a machine needs — `entitlement_updated`, a JSON blob of
 * before and after. An administrator reading the log needs a sentence. This is the
 * translation layer, kept on the client so the stored record stays raw and complete.
 */

export interface DescribedAudit {
  /** One-line summary, e.g. "Locked the account for Test User 2". */
  summary: string;
  /** What actually changed, e.g. "Seat limit 10 → 25". Empty when nothing to add. */
  detail: string;
}

function parse(value: string | null): Record<string, unknown> | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function moduleName(key: string | null): string {
  if (!key) return 'a module';
  return MODULE_CATALOG.find((m) => m.id === key)?.name ?? key;
}

/** Human labels for the fields that show up in change diffs. */
const FIELD_LABELS: Record<string, string> = {
  company_name: 'Company name',
  legal_name: 'Legal name',
  logo_url: 'Logo',
  email_domains: 'Email domains',
  locale: 'Locale',
  timezone: 'Timezone',
  support_contact: 'Support contact',
  phone: 'Phone',
  address_line1: 'Address line 1',
  address_line2: 'Address line 2',
  city: 'City',
  state_region: 'State / Region',
  postal_code: 'Postal code',
  country: 'Country',
  sic_codes: 'SIC codes',
  full_name: 'Name',
  email: 'Email',
  role: 'Role',
  is_active: 'Active',
  seat_limit: 'Seat limit',
  enabled: 'Licensed',
  term_start: 'Term start',
  term_end: 'Term end',
  issuer_url: 'Issuer URL',
  client_id: 'Client ID',
  redirect_uri: 'Redirect URI',
  protocol: 'Protocol',
  allow_local_fallback: 'Local sign-in fallback',
  auto_provision_users: 'Auto-provision users',
  default_role: 'Default role',

  /*
    APR's application and run-rate fields.
    
    These rows were reaching the screen through the `default` case below, which prints the
    stored JSON verbatim — a whole application record, forty-odd fields, in a one-line
    table cell. The labels are the column names as the APR screens themselves show them,
    so a diff here reads the same way the field does on the screen it was edited on.
  */
  application_id_code: 'Application ID',
  application_name: 'Application name',
  // The pre-rename spelling. Still present in rows written before the Q11 rename, so a
  // historical diff needs it as well as the current one.
  name: 'Application name',
  application_owner: 'Application owner',
  application_type: 'Application type',
  app_owner_email: 'Application owner email',
  bpo_email: 'Business process owner email',
  business_criticality: 'Business criticality',
  business_function_domain: 'Business function domain',
  business_process_owner: 'Business process owner',
  child_count: 'Child applications',
  cloud_migration: 'Cloud migration',
  consolidate: 'Consolidate',
  consolidate_target_app: 'Consolidate into',
  contract_end_date: 'Contract end date',
  current_operating: 'Current operating status',
  date_of_introduction: 'Date of introduction',
  description: 'Description',
  excluded_from_apr: 'Excluded from APR',
  executive_owner: 'Executive owner',
  executive_owner_email: 'Executive owner email',
  integration_dependencies: 'Integration dependencies',
  internal_number: 'Internal number',
  is_parent: 'Parent record',
  it_hardware_infrastructure: 'IT hardware / infrastructure',
  l1_business_functional_area: 'L1 business functional area',
  l2_capability_function: 'L2 capability / function',
  license_negotiation: 'Licence negotiation',
  license_renegotiation: 'Licence renegotiation',
  overall_score: 'Overall score',
  parent_application_id: 'Parent application',
  recommendation: 'Recommendation',
  replace_app: 'Replace',
  replace_target_app: 'Replace with',
  security_owner: 'Security owner',
  security_owner_email: 'Security owner email',
  technical_health_status: 'Technical health',
  technical_owner_custodian: 'Technical owner / custodian',
  technology_stack: 'Technology stack',
  to_email: 'Technical owner email',
  user_count: 'User count',
  vendor_supplier: 'Vendor / supplier',

  total_annual_cost: 'Total annual cost',
  annual_software_licensing_fees: 'Annual software licensing',
  annual_third_party_vendor_support: 'Annual third-party support',
  annual_infrastructure_cost: 'Annual infrastructure',
  indirect_operational_costs: 'Indirect operational costs',
  cloud_saas_subscription_cost_impact: 'Cloud / SaaS subscription',
  tco_status: 'TCO status',
};

const IGNORED_FIELDS = new Set([
  'updated_at',
  'updated_by',
  'created_at',
  'created_by',
  // A record's own primary key never changes, and printing it says nothing.
  'id',
  'has_client_secret',
]);

/*
  Fields whose 0 and 1 are QUANTITIES, not flags.

  `formatValue` maps 0 and 1 to "no" and "yes", which is right for AIR's integer flag
  columns and wrong for money: a run rate of 0 was reading as "Total annual cost: no",
  and a seat limit of 1 would read as "yes". Anything listed here is printed as a number.
*/
const NUMERIC_FIELDS = new Set([
  'total_annual_cost',
  'annual_software_licensing_fees',
  'annual_third_party_vendor_support',
  'annual_infrastructure_cost',
  'indirect_operational_costs',
  'cloud_saas_subscription_cost_impact',
  'overall_score',
  'user_count',
  'child_count',
  'seat_limit',
]);

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'none';
  // AIR's integer 0/1 flags read as yes/no, not as numbers.
  if (value === 0) return 'no';
  if (value === 1) return 'yes';
  return String(value);
}

/** `formatValue`, except that a field holding a quantity keeps its number. */
function formatFieldValue(key: string, value: unknown): string {
  if (NUMERIC_FIELDS.has(key)) {
    if (value === null || value === undefined || value === '') return 'empty';
    return String(value);
  }
  return formatValue(value);
}

/** Lists the fields that actually differ, as "Label: old → new". */
function diffFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  if (!before || !after) return [];
  const changes: string[] = [];

  for (const key of Object.keys(after)) {
    if (IGNORED_FIELDS.has(key)) continue;
    const oldValue = before[key];
    const newValue = after[key];
    if (JSON.stringify(oldValue ?? null) === JSON.stringify(newValue ?? null)) continue;
    changes.push(
      `${FIELD_LABELS[key] ?? key}: ${formatFieldValue(key, oldValue)} → ${formatFieldValue(key, newValue)}`,
    );
  }

  return changes;
}

/** The run-rate figures that matter at a glance, as a sentence fragment. */
function runRateDetail(values: Record<string, unknown> | null): string {
  if (!values) return '';
  const parts: string[] = [];
  if (values.total_annual_cost !== undefined) {
    parts.push(`Total annual cost: ${formatFieldValue('total_annual_cost', values.total_annual_cost)}`);
  }
  if (values.tco_status !== undefined) parts.push(`TCO status: ${formatValue(values.tco_status)}`);
  return parts.join(' · ');
}

/**
 * How an application record names itself in a sentence.
 *
 * Rows written before the Q11 rename carry `name`; rows written after carry
 * `application_name`. Both are read, newest spelling first, so a log that spans the
 * rename reads consistently instead of saying "an application" for half of it.
 */
function applicationLabel(values: Record<string, unknown> | null): string {
  if (!values) return 'an application';
  const code = values.application_id_code;
  const name = values.application_name ?? values.name;
  if (code && name) return `${String(code)} — ${String(name)}`;
  if (name) return String(name);
  if (code) return String(code);
  return 'an application';
}

/** `{created, updated, skipped}` counts, as a sentence fragment. Zeroes are kept. */
function countsDetail(values: Record<string, unknown> | null): string {
  if (!values) return '';
  const parts: string[] = [];
  for (const key of ['created', 'updated', 'skipped', 'deleted'] as const) {
    const value = values[key];
    if (typeof value === 'number') parts.push(`${value} ${key}`);
  }
  return parts.join(' · ');
}

export function describeAuditEntry(entry: AuditEntry): DescribedAudit {
  const before = parse(entry.old_values);
  const after = parse(entry.new_values);
  const who = entry.subject_name ?? (after?.email as string | undefined) ?? `#${entry.entity_id}`;

  switch (entry.action) {
    case 'bootstrap_admin_created':
      return {
        summary: `Provisioned the first administrator, ${formatValue(after?.email)}`,
        detail: 'Created automatically on first boot because the install had no users.',
      };

    case 'user_created':
      return {
        summary: `Created the user ${formatValue(after?.email)}`,
        detail: [
          `Role: ${formatValue(after?.role)}`,
          `Module access: ${formatValue(after?.modules)}`,
        ].join(' · '),
      };

    case 'user_updated': {
      const changes = diffFields(before, after);
      return {
        summary: `Updated the user ${who}`,
        detail: changes.length ? changes.join(' · ') : 'No fields changed.',
      };
    }

    case 'user_locked':
      return {
        summary: `Locked the account for ${who}`,
        detail: 'The user cannot sign in until an administrator unlocks the account.',
      };

    case 'user_unlocked':
      return {
        summary: `Unlocked the account for ${who}`,
        detail: 'Failed sign-in attempts were reset to zero.',
      };

    case 'user_password_reset':
      return {
        summary: `Reset the password for ${who}`,
        detail: 'The user must choose a new password at their next sign-in.',
      };

    /*
      Deleting an account. The row keeps the deleted person's details in `old_values`,
      which is the only place they survive — so the entry names them rather than leaving
      the log saying an unidentified user was removed.
    */
    case 'user_deleted':
      return {
        summary: `Deleted the user ${formatValue(before?.email ?? entry.subject_name)}`,
        detail: before
          ? [
              `Name: ${formatValue(before.full_name)}`,
              `Role: ${formatValue(before.role)}`,
            ].join(' · ')
          : 'No details were recorded for the deleted account.',
      };

    case 'user_modules_changed': {
      const previous = Array.isArray(before) ? (before as string[]) : [];
      const next = Array.isArray(after) ? (after as string[]) : [];
      const added = next.filter((m) => !previous.includes(m)).map(moduleName);
      const removed = previous.filter((m) => !next.includes(m)).map(moduleName);

      const parts: string[] = [];
      if (added.length) parts.push(`Granted: ${added.join(', ')}`);
      if (removed.length) parts.push(`Removed: ${removed.join(', ')}`);

      return {
        summary: `Changed module access for ${who}`,
        detail: parts.length ? parts.join(' · ') : 'No change.',
      };
    }

    case 'entitlement_updated': {
      const name = moduleName(entry.entity_id);
      const changes = diffFields(before, after);
      const licensingChanged = before && after && before.enabled !== after.enabled;

      return {
        summary: licensingChanged
          ? `${after?.enabled ? 'Licensed' : 'Unlicensed'} ${name}`
          : `Changed licensing for ${name}`,
        detail: changes.length ? changes.join(' · ') : 'No fields changed.',
      };
    }

    case 'company_profile_updated': {
      const changes = diffFields(before, after);
      return {
        summary: 'Updated the company profile',
        detail: changes.length ? changes.join(' · ') : 'No fields changed.',
      };
    }

    /*
      Open AI Integration. The stored rows never contain a key, so neither can these
      sentences — the audit row records only THAT a key was replaced or removed.
    */
    case 'ai_provider_updated': {
      const name = entry.entity_id ?? 'an AI provider';
      const parts: string[] = [];
      const settings = after?.settings;
      if (settings && typeof settings === 'object') {
        const pairs = Object.entries(settings as Record<string, unknown>).map(
          ([key, value]) => `${key}: ${formatValue(value)}`,
        );
        parts.push(pairs.length ? `Connection: ${pairs.join(', ')}` : 'Connection: empty');
      }
      if (after?.api_key_replaced === true) parts.push('API key replaced');
      if (after?.api_key_removed === true) parts.push('API key removed');
      return {
        summary: `Updated the ${name} AI provider settings`,
        detail: parts.join(' · '),
      };
    }

    case 'ai_default_provider_set':
      return {
        summary: after?.default_provider
          ? `Set the application-wide AI provider to ${formatValue(after.default_provider)}`
          : 'Cleared the application-wide AI provider',
        detail: `Was ${formatValue(before?.default_provider)}. Stored only — no module calls an AI provider yet.`,
      };

    case 'ai_module_provider_set':
      return {
        summary: after?.provider
          ? `Set ${moduleName(entry.entity_id)} to run on ${formatValue(after.provider)}`
          : `Set ${moduleName(entry.entity_id)} back to the application default AI provider`,
        detail: `Was ${before?.provider ? formatValue(before.provider) : 'the application default'}.`,
      };

    case 'sso_config_updated': {
      const changes = diffFields(before, after);
      if (after?.client_secret_changed === true) changes.push('Client secret replaced');
      return {
        summary: 'Updated single sign-on settings',
        detail: changes.length ? changes.join(' · ') : 'No fields changed.',
      };
    }


    /*
      APR module activity.

      These were falling through to `default` below, which prints `new_values` verbatim —
      so a one-line Details cell held an entire application record as raw JSON. The record
      itself is unchanged; this only renders it.
    */
    case 'application_created':
      return {
        summary: `Created the application ${applicationLabel(after)}`,
        detail: after?.description ? String(after.description) : 'No description recorded.',
      };

    case 'application_updated': {
      const changes = diffFields(before, after);
      return {
        summary: `Updated the application ${applicationLabel(after ?? before)}`,
        // A save that changed nothing is a real thing a user can do, and saying so is
        // more useful than an empty cell that looks like missing data.
        detail: changes.length ? changes.join(' · ') : 'No fields changed.',
      };
    }

    case 'application_deleted':
      return {
        summary: `Deleted the application ${applicationLabel(before)}`,
        detail: before?.description ? String(before.description) : '',
      };

    case 'applications_bulk_updated':
      return {
        summary: 'Bulk-updated applications',
        detail: countsDetail(after) || 'No counts recorded.',
      };

    case 'applications_imported':
      return {
        summary: 'Imported applications from a file',
        detail: countsDetail(after) || 'No counts recorded.',
      };

    case 'run_rate_created':
      return {
        summary: `Recorded the current run rate for ${formatValue(after?.application_name)}`,
        detail: runRateDetail(after),
      };

    case 'run_rate_updated': {
      const changes = diffFields(before, after);
      return {
        summary: `Updated the current run rate for ${formatValue(after?.application_name)}`,
        detail: changes.length ? changes.join(' · ') : 'No fields changed.',
      };
    }

    case 'run_rate_deleted':
      return {
        summary: `Deleted the current run rate for ${formatValue(before?.application_name)}`,
        detail: before ? runRateDetail(before) : '',
      };

    case 'run_rate_bulk_updated':
      return {
        summary: 'Bulk-updated current run rates',
        detail: countsDetail(after) || 'No counts recorded.',
      };

    case 'run_rate_imported':
      return {
        summary: 'Imported current run rates from a file',
        detail: countsDetail(after) || 'No counts recorded.',
      };

    /*
      Someone changing THEIR OWN password, which is a different event from an
      administrator resetting somebody else's (`user_password_reset` above). Neither
      records anything about the password itself, so there is no payload to describe.
    */
    case 'password_changed':
      return {
        summary: `${who} changed their own password`,
        detail: 'The password itself is never recorded.',
      };

    default:
      // An action added later still reads sensibly rather than showing a raw key.
      return {
        summary: entry.action.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        detail: entry.new_values ?? '',
      };
  }
}
